import { KalturaPlaylist } from "kaltura-typescript-client/api/types/KalturaPlaylist";
import { NameField } from "@kaltura/mediaspace-shared-ui";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { RichtextField } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { StyledDeleteButton, StyledForm, StyledPlaylistInfo } from "./styledComponents";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { normalizeText } from "../helpers";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface PlaylistInfoProps {
    playlist: KalturaPlaylist;
    onInfoUpdate?: (data: PlaylistFormData, isDirty: boolean) => void;
    onSave?: () => void;
    formValues?: PlaylistFormData;
}

export type PlaylistFormData = {
    name: string;
    description: string;
};

/**
 * PlaylistInfo Component - Handles the form for editing playlist information
 */
export const PlaylistInfo = (props: PlaylistInfoProps) => {
    const { playlist, onInfoUpdate, onSave, formValues } = props;
    const [openModal, setOpenModal] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();

    // Get the default values for the form
    const defaultValues = formValues || {
        name: playlist.name ?? "",
        description: playlist.description ?? "",
    };

    // Store the original values for direct comparison - reference these
    // instead of using formState.isDirty
    const [originalValues] = useState(defaultValues);

    const useFormHookMethods = useForm<PlaylistFormData>({
        defaultValues,
        mode: "onChange",
    });

    // Watch form values and notify parent of changes
    // This automatically subscribes to field changes and runs the callback
    useFormHookMethods.watch((formData) => {
        // Check if the current form data differs from the original values
        // This provides immediate change detection on the first character
        const currentData = {
            name: formData?.name || "",
            description: formData?.description || "",
        };

        const hasChanges =
            currentData.name !== originalValues.name
            || normalizeText(currentData.description) !== normalizeText(originalValues.description);

        onInfoUpdate?.(currentData, hasChanges);
    });

    // Handle form submission (when Enter is pressed or when the save button is clicked)
    const handleSubmit = useFormHookMethods.handleSubmit(() => {
        // Trigger the save action
        onSave?.();
    });

    // Handlers for the delete modal
    const handleOpenDeleteModal = () => {
        setOpenModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenModal(false);
    };

    const sendButtonAnalytics = useButtonAnalytics();

    const handleDeletePlaylist = async () => {
        sendButtonAnalytics(
            "Delete Playlist",
            ButtonClickAnalyticsType.DELETE,
            playlist.id,
            undefined,
            "Playlist edit - Info tab"
        );

        setProcessing(true);
        try {
            const result = await postKmsData(`${baseUrl}/default/playlists-ds/delete`, { id: playlist.id });
            if (result.success) {
                showToast({
                    severity: "success",
                    message: translate(`Playlist "%1" was deleted successfully`, [playlist.name]),
                });
                window.location.href = "/my-playlists";
            }
            else {
                handleCloseDeleteModal();
                showToast({ severity: "error", message: result.errorMessage });
            }
        }
        catch (error) {
            showToast({ severity: "error", message: translate(`Error deleting playlist "%1"`, [playlist.name]) });
            handleCloseDeleteModal();
        }
        finally {
            setProcessing(false);
        }
    };

    const baseClassName = "kms-ds-edit-playlist-info";

    return (
        <StyledPlaylistInfo className={baseClassName}>
            <FormProvider {...useFormHookMethods}>
                <StyledForm id="playlist-info-form" onSubmit={handleSubmit} className={`${baseClassName}-form`}>
                    <NameField id={"name"} label={translate("Name")} className={`${baseClassName}-form-name`} />
                    <RichtextField
                        label={translate("Description")}
                        id={"description"}
                        fontWeightBold={true}
                        inline={true}
                        minInputHeight={140}
                    />
                </StyledForm>
            </FormProvider>
            <StyledDeleteButton
                variant={"borderless"}
                onClick={handleOpenDeleteModal}
                className={`${baseClassName}-delete-button`}
            >
                {translate("Delete playlist")}
            </StyledDeleteButton>
            <Modal open={openModal} className={`${baseClassName}-delete-modal`}>
                <ModalTitle className={`${baseClassName}-delete-modal-title`}>
                    {translate("Delete Playlist")}
                </ModalTitle>
                <ModalContent className={`${baseClassName}-delete-modal-content`}>
                    <Typography>
                        {translate(`Are you sure you want to delete the playlist "%1"?`, [playlist.name])}
                    </Typography>
                </ModalContent>
                <ModalActions className={`${baseClassName}-delete-actions`}>
                    <Button
                        variant="borderless"
                        color="secondary"
                        onClick={handleCloseDeleteModal}
                        className={`${baseClassName}-delete-actions-button-cancel`}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleDeletePlaylist}
                        loading={processing}
                        className={`${baseClassName}-delete-actions-button-delete`}
                    >
                        {translate("Delete")}
                    </Button>
                </ModalActions>
            </Modal>
        </StyledPlaylistInfo>
    );
};
